import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PropertyCard from '../components/PropertyCard';
import DateBlockManager from '../components/DateBlockManager';
import { FiPlus, FiCalendar, FiAlertCircle, FiX, FiUser, FiMessageCircle } from 'react-icons/fi';
import './Dashboard.css';

const HostDashboard = () => {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuth();
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [ownershipRequests, setOwnershipRequests] = useState([]);
  const [selectedPropertyForBlocks, setSelectedPropertyForBlocks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Enable host mode if not already
    if (!user?.is_host) {
      enableHostMode();
    }
    fetchHostData();
  }, []);

  const enableHostMode = async () => {
    await updateProfile({ is_host: true });
    toast.success(t('hostDashboard.hostModeEnabled'));
  };

  const fetchHostData = async () => {
    try {
      const [propsResponse, bookingsResponse, requestsResponse] = await Promise.all([
        api.get(`/api/users/${user.id}/properties`),
        api.get('/api/bookings?type=host'),
        api.get('/api/hosts/ownership-requests').catch(() => ({ data: { ownership_requests: [] } }))
      ]);

      setProperties(propsResponse.data.properties);
      setBookings(bookingsResponse.data.bookings);
      setOwnershipRequests(requestsResponse.data.ownership_requests || []);
    } catch (error) {
      console.error('Error fetching host data:', error);
      toast.error(t('hostDashboard.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    if (!window.confirm(t('hostDashboard.confirmApproveRequest'))) return;

    try {
      await api.post(`/api/hosts/ownership-requests/${requestId}/approve`);
      toast.success(t('hostDashboard.requestApprovedSuccess'));
      fetchHostData();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(t('hostDashboard.errorApprovingRequest'));
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm(t('hostDashboard.confirmRejectRequest'))) return;

    try {
      await api.post(`/api/hosts/ownership-requests/${requestId}/reject`);
      toast.success(t('hostDashboard.requestRejected'));
      fetchHostData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(t('hostDashboard.errorRejectingRequest'));
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' && new Date(b.check_in) >= new Date());
  const pastBookings = bookings.filter(b => b.status === 'completed' || (b.status === 'cancelled' && new Date(b.check_out) < new Date()));
  const pendingRequests = ownershipRequests.filter(r => r.status === 'pending');

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>{t('hostDashboard.title')}</h1>
            <p className="dashboard-subtitle">{t('hostDashboard.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/host/reservations" className="btn btn-success">
              <FiCalendar /> {t('hostDashboard.manageReservations')}
            </Link>
            <Link to="/host/properties/new" className="btn btn-primary">
              <FiPlus /> {t('hostDashboard.addProperty')}
            </Link>
          </div>
        </div>

        {/* Pending Authorization Requests */}
        {pendingRequests.length > 0 && (
          <div className="alert alert-warning" style={{
            marginBottom: '2rem',
            background: '#fff8e1',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <FiAlertCircle size={24} color="#f57c00" />
              <h3 style={{ margin: 0 }}>Pedidos de Autorização Pendentes ({pendingRequests.length})</h3>
            </div>
            {pendingRequests.map(request => (
              <div key={request.id} style={{
                background: 'white',
                padding: '1.5rem',
                marginTop: '1rem',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <p><strong>{request.owner?.first_name} {request.owner?.last_name}</strong> quer que você gerencie a propriedade:</p>
                <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>{request.property?.title}</p>
                {request.message && (
                  <p style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>{request.message}</p>
                )}
                <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleApproveRequest(request.id)}
                  >
                    Aprovar
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleRejectRequest(request.id)}
                  >
                    Rejeitar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{properties.length}</h3>
            <p>{t('hostDashboard.activeProperties')}</p>
          </div>
          <div className="stat-card">
            <h3>{pendingBookings.length}</h3>
            <p>{t('hostDashboard.pendingRequests')}</p>
          </div>
          <div className="stat-card">
            <h3>{upcomingBookings.length}</h3>
            <p>{t('hostDashboard.upcomingBookings')}</p>
          </div>
          <div className="stat-card">
            <h3>R$ {bookings.reduce((sum, b) => sum + parseFloat(b.total_price || 0), 0).toFixed(0)}</h3>
            <p>{t('hostDashboard.totalRevenue')}</p>
          </div>
        </div>

        {/* Properties */}
        <div className="section">
          <h2 className="section-title">{t('hostDashboard.yourProperties')}</h2>
          {properties.length === 0 ? (
            <div className="no-properties">
              <p>{t('hostDashboard.noProperties')}</p>
              <Link to="/host/properties/new" className="btn btn-primary mt-3">
                <FiPlus /> {t('hostDashboard.listFirst')}
              </Link>
            </div>
          ) : (
            <div className="properties-grid">
              {properties.map((property) => {
                // Check if user is BOTH owner and host of this property
                const isOwnerAndHost = property.owner_info !== undefined;

                return (
                  <div key={property.id} className="property-with-actions">
                    <PropertyCard property={property} />

                    {/* Badge showing if user is owner+host */}
                    {isOwnerAndHost && (
                      <div style={{
                        background: '#e8f5e9',
                        border: '1px solid #4caf50',
                        borderRadius: '6px',
                        padding: '0.5rem',
                        marginTop: '0.5rem',
                        fontSize: '0.85rem',
                        color: '#2e7d32',
                        fontWeight: '600',
                        textAlign: 'center'
                      }}>
                        🏠 Você é proprietário e anfitrião deste imóvel
                      </div>
                    )}

                    <div className="property-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                      <Link
                        to={`/host/properties/${property.id}/edit`}
                        className="btn btn-secondary btn-sm"
                      >
                        {t('hostDashboard.edit')}
                      </Link>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => setSelectedPropertyForBlocks(property)}
                      >
                        <FiCalendar /> Gerenciar Calendário
                      </button>
                      {/* Show owner dashboard link if user is owner+host */}
                      {isOwnerAndHost && (
                        <Link
                          to={`/owner/properties/${property.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          📊 Ver Receita e Relatórios
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming Bookings */}
        {upcomingBookings.length > 0 && (
          <div className="section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 className="section-title" style={{ margin: 0 }}>Próximas Reservas</h2>
              <Link to="/host/reservations" className="btn btn-secondary btn-sm">
                {t('hostDashboard.viewAllReservations')}
              </Link>
            </div>
            <div className="bookings-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('hostDashboard.property')}</th>
                    <th>{t('hostDashboard.guest')}</th>
                    <th>{t('hostDashboard.checkIn')}</th>
                    <th>Check-out</th>
                    <th>{t('hostDashboard.nights')}</th>
                    <th>{t('hostDashboard.total')}</th>
                    <th>{t('hostDashboard.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingBookings.slice(0, 10).map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.property.title}</td>
                      <td>{booking.guest.first_name} {booking.guest.last_name}</td>
                      <td>{new Date(booking.check_in).toLocaleDateString('pt-BR')}</td>
                      <td>{new Date(booking.check_out).toLocaleDateString('pt-BR')}</td>
                      <td>{booking.nights}</td>
                      <td>R$ {booking.total_price}</td>
                      <td>
                        <span className={`badge badge-success`}>
                          Confirmada
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <div className="section">
            <h2 className="section-title">Reservas Passadas</h2>
            <div className="bookings-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('hostDashboard.property')}</th>
                    <th>{t('hostDashboard.guest')}</th>
                    <th>{t('hostDashboard.checkIn')}</th>
                    <th>Check-out</th>
                    <th>{t('hostDashboard.nights')}</th>
                    <th>{t('hostDashboard.total')}</th>
                    <th>{t('hostDashboard.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pastBookings.slice(0, 10).map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.property.title}</td>
                      <td>{booking.guest.first_name} {booking.guest.last_name}</td>
                      <td>{new Date(booking.check_in).toLocaleDateString('pt-BR')}</td>
                      <td>{new Date(booking.check_out).toLocaleDateString('pt-BR')}</td>
                      <td>{booking.nights}</td>
                      <td>R$ {booking.total_price}</td>
                      <td>
                        <span className={`badge badge-${booking.status === 'completed' ? 'success' : 'secondary'}`}>
                          {booking.status === 'completed' ? 'Concluída' : booking.status === 'cancelled' ? 'Cancelada' : t(`booking.${booking.status}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Date Block Management Modal */}
        {selectedPropertyForBlocks && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative'
            }}>
              <div style={{
                position: 'sticky',
                top: 0,
                background: 'white',
                padding: '1.5rem',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 1
              }}>
                <h2 style={{ margin: 0 }}>Gerenciar Calendário - {selectedPropertyForBlocks.title}</h2>
                <button
                  onClick={() => setSelectedPropertyForBlocks(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    color: '#666'
                  }}
                >
                  <FiX />
                </button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <DateBlockManager
                  propertyId={selectedPropertyForBlocks.id}
                  apiEndpoint="/api/hosts"
                  onUpdate={() => {
                    toast.success('Calendário atualizado!');
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostDashboard;
